import { useState } from 'react';
import { Search, RefreshCw, ShieldAlert, User, Users, Info, ChevronRight, AlertTriangle } from 'lucide-react';
import { api } from '@/contexts/AuthContext';
import BackNavButton from '@/components/BackNavButton';

/**
 * Admin-only scoring diagnostic page. Consumes:
 *   GET  /api/admin/scoring/diagnose?user_id=…
 *   GET  /api/admin/scoring/diagnose-pair?user_a=…&user_b=…
 *   GET  /api/admin/scoring/audit?user_id=…
 *   POST /api/admin/scoring/recalculate/{user_id}
 *
 * Purpose:
 *   - Verify live vs stored score for any user (stale detection).
 *   - Inspect raw activity counters that feed the formula.
 *   - Force-recompute a user's scores.
 *   - Compare two users' compat with full breakdown.
 *   - Read the last N audit-log entries for a user.
 *
 * This surface is INTERNAL. Do not link from user-facing pages.
 */
export default function AdminScoring() {
  const [mode, setMode] = useState('user'); // 'user' | 'pair'
  const [userA, setUserA] = useState('');
  const [userB, setUserB] = useState('');
  const [diagnose, setDiagnose] = useState(null);
  const [audit, setAudit] = useState(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);

  const runDiagnose = async () => {
    setLoading(true); setError(null); setDiagnose(null); setAudit(null);
    try {
      if (mode === 'user') {
        if (!userA.trim()) throw new Error('user_id required');
        const r = await api.get(`/admin/scoring/diagnose?user_id=${encodeURIComponent(userA.trim())}`);
        setDiagnose(r.data);
        // Also load audit log tail
        const a = await api.get(`/admin/scoring/audit?user_id=${encodeURIComponent(userA.trim())}&limit=20`);
        setAudit(a.data);
      } else {
        if (!userA.trim() || !userB.trim()) throw new Error('both user_ids required');
        const r = await api.get(`/admin/scoring/diagnose-pair?user_a=${encodeURIComponent(userA.trim())}&user_b=${encodeURIComponent(userB.trim())}`);
        setDiagnose(r.data);
      }
    } catch (e) {
      setError(e.response?.data?.detail || e.message || 'Failed');
    } finally { setLoading(false); }
  };

  const forceRecalc = async () => {
    if (mode !== 'user' || !userA.trim()) return;
    setLoading(true); setError(null);
    try {
      await api.post(`/admin/scoring/recalculate/${encodeURIComponent(userA.trim())}`);
      await runDiagnose();
    } catch (e) {
      setError(e.response?.data?.detail || e.message || 'Recalc failed');
    } finally { setLoading(false); }
  };

  return (
    <div className="min-h-screen bg-white pb-16" data-testid="admin-scoring-page">
      {/* Header */}
      <div className="px-5 pt-8 pb-4 flex items-center gap-3 sticky top-0 bg-white/95 backdrop-blur-md z-20 border-b border-[var(--hairline)]">
        <BackNavButton />
        <h1 className="font-brand text-[26px] font-[600] tracking-tight">Scoring</h1>
        <span className="ml-auto inline-flex items-center gap-1 text-[10px] font-bold uppercase tracking-wider text-[#B91C1C]">
          <ShieldAlert size={11} /> Admin
        </span>
      </div>

      {/* Mode switch */}
      <div className="px-5 pt-4 flex gap-2">
        <button data-testid="mode-user" onClick={() => setMode('user')}
          className={`px-3 py-1.5 rounded-full text-[12px] font-semibold border transition ${mode === 'user' ? 'brand-gradient text-white border-transparent' : 'bg-white border-[var(--hairline-strong)] text-[#4B4B52]'}`}>
          <User size={12} className="inline mr-1" /> Single user
        </button>
        <button data-testid="mode-pair" onClick={() => setMode('pair')}
          className={`px-3 py-1.5 rounded-full text-[12px] font-semibold border transition ${mode === 'pair' ? 'brand-gradient text-white border-transparent' : 'bg-white border-[var(--hairline-strong)] text-[#4B4B52]'}`}>
          <Users size={12} className="inline mr-1" /> Pair compat
        </button>
      </div>

      {/* Input */}
      <div className="px-5 pt-3 flex flex-col gap-2">
        <div className="relative">
          <Search size={14} className="absolute left-3 top-1/2 -translate-y-1/2 text-[#8A8A93]" />
          <input
            data-testid="scoring-input-a"
            value={userA} onChange={(e) => setUserA(e.target.value)}
            placeholder={mode === 'user' ? 'user_id (e.g. user_abc123)' : 'user_a_id'}
            className="w-full pl-9 pr-3 py-2 rounded-lg border border-[var(--hairline-strong)] text-[13px]"
          />
        </div>
        {mode === 'pair' && (
          <div className="relative">
            <Search size={14} className="absolute left-3 top-1/2 -translate-y-1/2 text-[#8A8A93]" />
            <input
              data-testid="scoring-input-b"
              value={userB} onChange={(e) => setUserB(e.target.value)}
              placeholder="user_b_id"
              className="w-full pl-9 pr-3 py-2 rounded-lg border border-[var(--hairline-strong)] text-[13px]"
            />
          </div>
        )}
        <div className="flex gap-2">
          <button data-testid="scoring-run" onClick={runDiagnose} disabled={loading}
            className="flex-1 py-2 rounded-lg bg-[#0F0F10] text-white text-[12px] font-semibold disabled:opacity-50">
            {loading ? <RefreshCw size={13} className="inline animate-spin mr-1" /> : <Search size={13} className="inline mr-1" />}
            Diagnose
          </button>
          {mode === 'user' && (
            <button data-testid="scoring-recalc" onClick={forceRecalc} disabled={loading || !userA.trim()}
              className="px-4 py-2 rounded-lg border border-[var(--hairline-strong)] text-[12px] font-semibold text-[#0F0F10] disabled:opacity-50">
              Force recalc
            </button>
          )}
        </div>
        {error && (
          <div className="text-[12px] text-[#B91C1C] flex items-center gap-1.5" data-testid="scoring-error">
            <AlertTriangle size={12} /> {error}
          </div>
        )}
      </div>

      {/* Results */}
      {diagnose && (
        <div className="px-5 pt-6 space-y-4">
          {mode === 'user' ? (
            <UserDiagnose data={diagnose} audit={audit} />
          ) : (
            <PairDiagnose data={diagnose} />
          )}
        </div>
      )}

      {/* Legend */}
      {!diagnose && (
        <div className="px-5 pt-8 space-y-4 max-w-md">
          <BackfillPanel />
          <div className="text-[12px] text-[#8A8A93]" data-testid="scoring-legend">
          <div className="flex items-start gap-2">
            <Info size={13} className="mt-0.5 flex-shrink-0" />
            <div>
              <p className="font-semibold text-[#0F0F10] mb-1">What this shows</p>
              <ul className="space-y-1 list-disc list-inside">
                <li><b>Stored</b> = the last score written to <code>user_scores</code> / <code>compatibility_scores</code>.</li>
                <li><b>Live</b> = what the engine would return right now from the raw data.</li>
                <li><b>Stale</b> = stored ≠ live, or the version is behind. Force-recalc to sync.</li>
                <li><b>Raw activity</b> = every counter the formula reads (messages, calls, safe-meets, etc).</li>
                <li><b>Audit tail</b> = last 20 recomputations with previous → new deltas.</li>
              </ul>
            </div>
          </div>
          </div>
        </div>
      )}
    </div>
  );
}


function StaleBadge({ stale }) {
  return (
    <span
      data-testid={`stale-badge-${stale ? 'yes' : 'no'}`}
      className={`inline-flex items-center gap-1 px-2 py-0.5 rounded-full text-[10px] font-bold uppercase tracking-wider ${stale ? 'bg-[#B91C1C] text-white' : 'bg-emerald-500 text-white'}`}>
      {stale ? 'Stale' : 'Fresh'}
    </span>
  );
}


function BackfillPanel() {
  const [running, setRunning] = useState(false);
  const [result, setResult] = useState(null);
  const [target, setTarget] = useState('pairs');
  const [limit, setLimit] = useState(500);
  const [dryRun, setDryRun] = useState(true);

  const run = async () => {
    setRunning(true); setResult(null);
    try {
      const r = await api.post(`/admin/scoring/backfill?target=${target}&limit=${limit}&dry_run=${dryRun}`);
      setResult(r.data);
    } catch (e) {
      setResult({ error: e.response?.data?.detail || e.message });
    } finally { setRunning(false); }
  };

  return (
    <div className="rounded-2xl border border-amber-300 bg-amber-50 p-4" data-testid="backfill-panel">
      <p className="text-[11px] uppercase tracking-[0.14em] text-amber-800 font-bold mb-2">Backfill v2 scores</p>
      <p className="text-[11px] text-amber-900 mb-3">
        Recompute v2 scores for every active match (pair compat) and/or every non-excluded user (meet + responsiveness).
        Idempotent — safe to run repeatedly.
      </p>
      <div className="flex flex-wrap gap-2 mb-3">
        {['pairs', 'users', 'both'].map(t => (
          <button key={t} onClick={() => setTarget(t)} data-testid={`backfill-target-${t}`}
            className={`px-3 py-1 rounded-full text-[11px] font-semibold border transition ${target === t ? 'bg-amber-600 text-white border-transparent' : 'bg-white text-amber-900 border-amber-300'}`}>
            {t}
          </button>
        ))}
        <label className="flex items-center gap-1 text-[11px] text-amber-900">
          Limit:
          <input type="number" value={limit} onChange={(e) => setLimit(Number(e.target.value) || 500)}
            data-testid="backfill-limit"
            className="w-20 px-2 py-0.5 rounded border border-amber-300 bg-white text-[11px] tabular-nums" />
        </label>
        <label className="flex items-center gap-1 text-[11px] text-amber-900">
          <input type="checkbox" checked={dryRun} onChange={(e) => setDryRun(e.target.checked)} data-testid="backfill-dryrun" />
          Dry run
        </label>
      </div>
      <button data-testid="backfill-run" onClick={run} disabled={running}
        className="w-full py-1.5 rounded-lg bg-amber-600 text-white text-[12px] font-semibold disabled:opacity-50">
        {running ? 'Running…' : `Run backfill (${target}, ${dryRun ? 'dry' : 'live'})`}
      </button>
      {result && (
        <pre className="mt-3 text-[10.5px] text-amber-950 whitespace-pre-wrap break-all overflow-auto max-h-52 bg-white/70 rounded-lg p-2" data-testid="backfill-result">
{JSON.stringify(result, null, 2)}
        </pre>
      )}
    </div>
  );
}


function UserDiagnose({ data, audit }) {
  const stored = data.stored || {};
  const liveMeet = data.live?.meet || {};
  const liveResp = data.live?.responsiveness || {};

  return (
    <>
      {/* Summary card */}
      <div className="rounded-2xl border border-[var(--hairline)] bg-white p-4" data-testid="user-diagnose-summary">
        <div className="flex items-center gap-2 mb-3">
          <p className="text-[10.5px] uppercase tracking-[0.14em] text-[#8A8A93] font-semibold">User</p>
          <p className="text-[13px] font-semibold text-[#0F0F10] truncate">{data.user_id}</p>
          <StaleBadge stale={!!data.stale} />
          {data.excluded && (
            <span className="inline-flex items-center gap-1 px-2 py-0.5 rounded-full text-[10px] font-bold uppercase tracking-wider bg-black text-white">
              Excluded
            </span>
          )}
        </div>
        <div className="grid grid-cols-2 gap-3">
          <ScoreCompare label="Meet Score"     stored={stored.meet_score}          live={liveMeet.score}  band={liveMeet.band} isNew={liveMeet.is_new} confidence={liveMeet.confidence} />
          <ScoreCompare label="Responsiveness" stored={stored.responsiveness_score} live={liveResp.score} band={liveResp.band} isNew={liveResp.is_new} confidence={liveResp.confidence} />
        </div>
        <p className="mt-3 text-[10.5px] text-[#8A8A93]">Score version: <b>{data.score_version}</b>{stored.updated_at ? ` · stored updated ${stored.updated_at.slice(0, 19).replace('T', ' ')}` : ''}</p>
      </div>

      {/* Meet breakdown */}
      <BreakdownSection title="Meet Score breakdown (live)" testId="meet-breakdown" breakdown={liveMeet.breakdown} />
      {/* Responsiveness breakdown */}
      <BreakdownSection title="Responsiveness breakdown (live)" testId="resp-breakdown" breakdown={liveResp.breakdown} />

      {/* Raw activity */}
      <div className="rounded-2xl border border-[var(--hairline)] bg-[var(--surface-2)] p-4" data-testid="raw-activity">
        <p className="text-[10.5px] uppercase tracking-[0.14em] text-[#8A8A93] font-semibold mb-2">Raw activity (both engines)</p>
        <pre className="text-[10.5px] leading-relaxed text-[#4B4B52] whitespace-pre-wrap break-all overflow-auto max-h-72">
{JSON.stringify(data.raw_activity, null, 2)}
        </pre>
      </div>

      {/* Audit tail */}
      {audit && (audit.logs || []).length > 0 && (
        <div className="rounded-2xl border border-[var(--hairline)] bg-white p-4" data-testid="audit-tail">
          <p className="text-[10.5px] uppercase tracking-[0.14em] text-[#8A8A93] font-semibold mb-2">Audit tail — last {audit.count}</p>
          <div className="space-y-2">
            {audit.logs.slice(0, 20).map((log, i) => (
              <div key={i} className="border-b border-[var(--hairline)] pb-2 last:border-0">
                <div className="flex items-center gap-2 text-[11px]">
                  <span className="text-[#8A8A93]">{(log.created_at || '').slice(0, 19).replace('T', ' ')}</span>
                  <span className="font-semibold text-[#0F0F10]">{log.trigger_event || '—'}</span>
                </div>
                <div className="text-[10.5px] text-[#4B4B52] mt-0.5">
                  Meet: {log.meet_previous ?? '·'} → <b>{log.meet_new}</b> · Resp: {log.responsiveness_previous ?? '·'} → <b>{log.responsiveness_new}</b>
                </div>
              </div>
            ))}
          </div>
        </div>
      )}
    </>
  );
}


function PairDiagnose({ data }) {
  const stored = data.stored || {};
  const live = data.live || {};
  return (
    <>
      <div className="rounded-2xl border border-[var(--hairline)] bg-white p-4" data-testid="pair-diagnose-summary">
        <div className="flex items-center gap-2 mb-2">
          <p className="text-[10.5px] uppercase tracking-[0.14em] text-[#8A8A93] font-semibold">Pair</p>
          <p className="text-[12px] font-mono text-[#4B4B52] truncate">{data.pair_id}</p>
          <StaleBadge stale={!!data.stale} />
        </div>
        <div className="flex items-center gap-3 mt-1">
          <ScoreCompare label="Compatibility" stored={stored.compatibility_score} live={live.score} />
          {live.capped_from != null && (
            <span className="inline-flex items-center gap-1 px-2 py-0.5 rounded-full text-[10px] font-bold uppercase tracking-wider bg-amber-500 text-white">
              Capped ← {live.capped_from}
            </span>
          )}
        </div>
        {(live.dealbreaker_flags || []).length > 0 && (
          <div className="mt-2 flex flex-wrap gap-1.5" data-testid="dealbreaker-flags">
            {live.dealbreaker_flags.map((f) => (
              <span key={f} className="px-2 py-0.5 rounded-full text-[10px] font-semibold bg-[#FEE2E2] text-[#B91C1C]">{f}</span>
            ))}
          </div>
        )}
        {live.distance_km != null && (
          <p className="mt-2 text-[11px] text-[#8A8A93]">Distance: <b>{live.distance_km} km</b></p>
        )}
      </div>

      <BreakdownSection title="Compatibility breakdown (live)" testId="compat-breakdown" breakdown={live.breakdown} />
    </>
  );
}


function ScoreCompare({ label, stored, live, band, isNew, confidence }) {
  const diff = (stored != null && live != null) ? (live - stored) : null;
  return (
    <div className="rounded-lg bg-[var(--surface-2)] p-3">
      <p className="text-[10.5px] uppercase tracking-[0.14em] text-[#8A8A93] font-semibold">{label}</p>
      <div className="flex items-baseline gap-2 mt-1">
        <p className="text-[22px] font-bold tabular-nums text-[#0F0F10]">{live ?? '—'}</p>
        <p className="text-[11px] text-[#8A8A93] tabular-nums">stored: {stored ?? '—'}</p>
        {diff != null && diff !== 0 && (
          <span className={`text-[10.5px] font-semibold ${diff > 0 ? 'text-emerald-600' : 'text-[#B91C1C]'}`}>
            {diff > 0 ? '+' : ''}{diff}
          </span>
        )}
      </div>
      {(band || isNew) && (
        <p className="text-[10.5px] text-[#4B4B52] mt-0.5">{isNew ? 'New' : band}{confidence != null ? ` · conf ${(confidence * 100).toFixed(0)}%` : ''}</p>
      )}
    </div>
  );
}


function BreakdownSection({ title, testId, breakdown }) {
  if (!breakdown) return null;
  const rows = Object.entries(breakdown);
  return (
    <div className="rounded-2xl border border-[var(--hairline)] bg-white p-4" data-testid={testId}>
      <p className="text-[10.5px] uppercase tracking-[0.14em] text-[#8A8A93] font-semibold mb-2">{title}</p>
      <div className="space-y-1.5">
        {rows.map(([cat, val]) => {
          const pts = val?.points ?? val?.score ?? 0;
          const weight = val?.weight ?? 0;
          const pct = weight > 0 ? (pts / weight) : 0;
          return (
            <div key={cat} className="flex items-center gap-2">
              <p className="text-[11.5px] font-semibold text-[#0F0F10] w-40 flex-shrink-0 truncate">{cat.replace(/_/g, ' ')}</p>
              <div className="flex-1 h-1.5 rounded-full bg-[var(--surface-2)] overflow-hidden">
                <div className="h-full bg-[#0F0F10]" style={{ width: `${Math.min(100, pct * 100)}%` }} />
              </div>
              <p className="text-[11px] tabular-nums text-[#4B4B52] w-20 text-right">{pts.toFixed(1)} / {weight}</p>
              <ChevronRight size={12} className="text-[#8A8A93]" />
            </div>
          );
        })}
      </div>
    </div>
  );
}
